/**
 * TOKENS UTENFRA: HVA EN AI ELLER EN INTEGRASJON FÅR GJØRE (revisjonen av MCP-en, Atle 02.11).
 *
 * Effektiv tilgang = brukerens rettigheter ∩ organisasjonens AI-brytere ∩ SPERRENE. Brukerens rettigheter
 * er rollematrisen som før, og `can()` spør denne fila når aktøren har `external`. Den gjelder alle veier
 * inn — MCP-serveren, et integrasjonstoken — og også superadmin.
 *
 * FØR: alt som ikke var nevnt, var sperret, og mye av plattformen var derfor utenfor AI-en. NÅ er det
 * motsatt: AI-en kan alt brukeren kan, unntatt SPERRENE under. De er det eneste som skiller AI-en fra
 * brukeren den handler for:
 *
 *   ALDRI — slette organisasjon, festival, årgang, konsert, programpost, artist, bruker eller spillested;
 *           signere, sende til signering, annullere eller endre vilkår i kontrakter; endre
 *           organisasjonsinnstillinger, arkivere, slette persondata, endre passord eller e-post, eller
 *           gjøre noe i Control Room.
 *   GODKJENNING — godta eller avslå bud, endre bekreftede priser, provisjon og oppgjør; sende e-post eller
 *           SMS ut av Tvitvi. Serveren krever en bekreftelse i to steg: kallet uten bekreftelse svarer
 *           `AI_CONFIRMATION_REQUIRED` med et token og nøyaktig hva som skal skje, og det samme kallet med
 *           tokenet (`X-Tvitvi-Confirmation`) utføres. Tokenet er bundet til tilkoblingen, metoden, stien
 *           og innholdet, og kan brukes én gang.
 *
 * TO NIVÅER, SOM FØR: handlingen her er den grove porten, og rutetabellen i API-et
 * (`apps/api/src/policy/external-routes.ts`) den fine, fordi mange ruter står på en handling som ikke sier
 * hva de gjør (å sende kontrakten til signering står på `contract:draft`).
 */
import type { Action, Actor, Resource } from './types.js';

/** Klienten bak et token utenfra. Navnet kommer fra hvitelista, aldri fra klienten selv. */
export interface ExternalClient {
  readonly connectionId: string;
  readonly clientType: 'ai' | 'integration';
  /** 'Claude', 'ChatGPT', 'Grok', 'VS Code', '<navn> (lokal)' … */
  readonly clientName: string;
  /** Organisasjonene brukeren huket av på godkjenningssiden (279.5 a). */
  readonly organizationIds: readonly string[];
  /**
   * Kallet bærer en gyldig, ubrukt bekreftelse for nøyaktig denne forespørselen (`X-Tvitvi-Confirmation`).
   * Satt av API-et når tokenet slås opp; aldri av klienten.
   */
  readonly confirmed?: boolean;
  /**
   * Kallet er lesing (GET/HEAD). Da gjelder ikke sperrene på handlingen: en leserute som står på en
   * skrivehandling (kandidatene til sammenslåing står på `contact:merge`), er lesing. Control Room er sperret
   * på ruta, også for lesing. Satt av API-et.
   */
  readonly reading?: boolean;
}

/** Organisasjonens to brytere (B4), lest ved hvert kall for den aktive organisasjonen. */
export interface AiAccessSwitches {
  readonly access: boolean;
  /** Bare `true` når `access` også er det. */
  readonly write: boolean;
}

/** `allowed`: som brukeren. `approval`: bare med bekreftelse. `never`: aldri for en AI. */
export type ExternalAccess = 'allowed' | 'approval' | 'never';

export const EXTERNAL_DENY_REASONS = [
  'ai_access_disabled',
  'ai_write_disabled',
  'external_blocked',
  'ai_confirmation_required',
  'external_draft_only',
] as const;
export type ExternalDenyReason = (typeof EXTERNAL_DENY_REASONS)[number];

export const isExternalDenyReason = (value: string): value is ExternalDenyReason =>
  (EXTERNAL_DENY_REASONS as readonly string[]).includes(value);

/**
 * SPERRENE PER HANDLING. Det som ikke står her, er `allowed`. Rutetabellen i API-et har resten: ruter
 * som står på en handling som ikke sier hva de gjør, og Control Room.
 */
export const EXTERNAL_NEVER_ACTIONS: ReadonlySet<Action> = new Set<Action>([
  // Slette hovedobjekter.
  'organization:delete',
  'festival:delete',
  'edition:delete',
  'concert:delete',
  'performance:delete',
  'booking_artist:delete',
  'venue:delete',
  'venue_night:delete',
  // Kontrakter: endre vilkår, annullere, registrere som signert, hvem som signerer.
  'contract:update',
  'contract:void',
  'contract:register',
  'contract:manage_roles',
  // Organisasjonsinnstillinger, arkivering, persondata, konto.
  'organization:update',
  'edition:archive',
  'artist:archive',
  'contact:delete',
  'contact:merge',
  'user:update',
]);

export const EXTERNAL_APPROVAL_ACTIONS: ReadonlySet<Action> = new Set<Action>([
  // Godta eller avslå bud, motbud og endrede vilkår i avtalearket.
  'booking_bid:respond',
  // Bekreftede priser, provisjon og oppgjør.
  'economy:create',
  'economy:update',
  'economy:delete',
  'concert_budget:update',
  'venue_rate:update',
  // E-post ut av Tvitvi til et byrå uten konto.
  'booking_enquiry:notify',
]);

/** Lesing: det `ai_write = false` slipper gjennom. Eksport er lesing. */
const READ_VERBS: ReadonlySet<string> = new Set(['list', 'read', 'access_all']);
const READ_ACTIONS: ReadonlySet<Action> = new Set<Action>(['export:create']);

export function isReadAction(action: Action): boolean {
  if (READ_ACTIONS.has(action)) return true;
  const verb = action.slice(action.indexOf(':') + 1);
  return READ_VERBS.has(verb);
}

export function externalAccessOf(action: Action): ExternalAccess {
  if (EXTERNAL_NEVER_ACTIONS.has(action)) return 'never';
  if (EXTERNAL_APPROVAL_ACTIONS.has(action)) return 'approval';
  return 'allowed';
}

/** Bryterne først, så sperrene. `true` er ja; ellers grunnen. Rollematrisen er `can()` sin. */
export function externalDecision(
  actor: Pick<Actor, 'aiAccess' | 'external'>,
  action: Action,
  _resource?: Resource,
): true | ExternalDenyReason {
  const switches = actor.aiAccess;
  if (switches === undefined || !switches.access) return 'ai_access_disabled';
  if (actor.external?.reading === true) return true;
  const access = externalAccessOf(action);
  if (access === 'never') return 'external_blocked';
  if (!isReadAction(action) && !switches.write) return 'ai_write_disabled';
  if (access === 'approval' && actor.external?.confirmed !== true) return 'ai_confirmation_required';
  return true;
}

/**
 * `draft_only`: AI-en får lagre et utkast brukeren selv sender, men ikke sende (Atle 02.11 kveld: «meldinger
 * i interne tråder sendes direkte; til andre organisasjoner kun som utkast»). Bare for operasjonene under.
 */
export type ExternalOperationAccess = ExternalAccess | 'draft_only';

/**
 * DET RUTA OG HANDLINGEN IKKE SER: en variant av en tillatt handling som avgjøres av raden eller
 * kroppen. Tjenesten spør når den vet det, med navnet her — ett sted, så listen kan leses.
 */
export const EXTERNAL_OPERATIONS = {
  /** En melding (eller reaksjon, eller endring) i en tråd en annen organisasjon leser: bare utkast. */
  'message.in_shared_thread': 'draft_only',
  /**
   * Å legge en person fra en annen organisasjon inn i en tråd. Krever ja (03.11): en ny tråd sender den første
   * meldingen med en gang, og en lagt til person leser hele tråden.
   */
  'thread.add_outside_participant': 'approval',
  /** Å slette et vedlegg en annen har lastet opp. */
  'attachment.delete_others': 'allowed',
  /** Persontypene §3.3 (1090): «Send lenken» når en frivillig legges til, er en e-post ut av Tvitvi. */
  'volunteer.send_link': 'approval',
  /** 290.5 (1160): å avlyse sender e-post og stenger en side; å angre åpner den. */
  'volunteer.cancel': 'approval',
} as const satisfies Record<string, ExternalOperationAccess>;
export type ExternalOperation = keyof typeof EXTERNAL_OPERATIONS;

/** `true` når aktøren ikke er ekstern, eller operasjonen er tillatt; ellers grunnen. */
export function externalOperationDecision(
  actor: Pick<Actor, 'external' | 'aiAccess'>,
  operation: ExternalOperation,
): true | ExternalDenyReason {
  if (actor.external === undefined) return true;
  const access = EXTERNAL_OPERATIONS[operation] as ExternalOperationAccess;
  if (access === 'never') return 'external_blocked';
  if (access === 'draft_only') return 'external_draft_only';
  if (access === 'approval' && actor.external.confirmed !== true) return 'ai_confirmation_required';
  return true;
}
